<template>
  <Dialog
      v-model:visible="visible"
      modal
      :style="{ width: '1000px', maxWidth: '95vw' }"
      :header="dialogTitle"
      @show="onShow"
  >
    <div class="flex items-center gap-3 mb-4">
      <Dropdown
          v-if="remainingLangs.length"
          :options="remainingLangs"
          placeholder="Dil ekle"
          class="w-40"
          @change="onAddLanguage($event.value)"
      />
      <Button v-else label="Tüm diller eklendi" severity="secondary" size="small" disabled />
      <span class="ml-auto text-sm opacity-70" v-if="loading">Yükleniyor…</span>
    </div>

    <TabView v-model:activeIndex="activeIndex"  @tab-change="onTabChange">
      <TabPanel
          v-for="entry in formEntries"
          :key="entry.language"
          :header="tabHeader(entry)"
      >
        <div class="grid grid-cols-12 gap-4">
          <!-- DİNAMİK ALANLAR -->
          <template v-for="field in formFields" :key="field.model">
            <div v-if="!field.disable" class="col-span-12" :class="field.colClass || 'md:col-span-6'">
              <label class="block text-sm font-medium mb-1">
                {{ field.label }}
                <span v-if="field.required" class="text-red-500">*</span>
              </label>

              <!-- Text -->
              <InputText
                  v-if="field.type==='text'"
                  v-model.trim="entry[field.model]"
                  :placeholder="field.placeholder"
                  class="w-100"
                  :disabled="field.disable"
                  @input="markDirty(entry)"
              />

              <!-- Number -->
              <InputNumber
                  v-else-if="field.type==='number'"
                  v-model="entry[field.model]"
                  :min="field.min"
                  :max="field.max"
                  :step="field.step || 1"
                  showButtons
                  buttonLayout="horizontal"
                  class="w-100"
                  :disabled="field.disable"
                  @update:modelValue="markDirty(entry)"
              />

              <!-- Textarea -->
              <Textarea
                  v-else-if="field.type==='textarea'"
                  v-model.trim="entry[field.model]"
                  :rows="field.rows || 6"
                  autoResize
                  class="w-100"
                  :placeholder="field.placeholder"
                  :disabled="field.disable"
                  @input="markDirty(entry)"
              />

              <!-- Switch -->
              <div v-else-if="field.type==='switch'" class="flex items-center gap-3">
                <InputSwitch v-model="entry[field.model]" @change="markDirty(entry)" :disabled="field.disable"/>
                <span class="opacity-80">{{ entry[field.model] ? (field.trueText || 'Evet') : (field.falseText || 'Hayır') }}</span>
              </div>

              <div v-else-if="field.type==='chips'">
                <template v-if="!entry.__isNew">
                  <div class="row mb-4">
                    <div class="col-3">
                      <InputText class="w-100" type="text" v-model="newAstrologTag.tag"/>
                    </div>
                    <div class="col-6">
                      <Button class="d-inline" label="Add Tag" icon="pi pi-plus" @click="addNewAstrologTag(entry)"/>
                    </div>
                    <label for="" class="fw-light">Manuel olarak giriniz.</label>
                  </div>
                  <Listbox
                      :options="astrologTags"
                      optionLabel="tag"
                      optionValue="id"
                      class="w-full"
                  >
                    <template #option="{ option }">
                      <div class="d-flex justify-content-between align-items-center w-full gap-2">
                        <span class="truncate">{{ option.tag }}</span>
                        <Button
                            icon="pi pi-trash"
                            class="p-button-sm p-button-danger"
                            @click.stop="deleteAstrologTag(option.id)"
                        />
                      </div>
                    </template>

                    <template #empty>
                      <div class="p-3 text-center text-sm opacity-70">
                        Kayıtlı etiket yok.
                      </div>
                    </template>
                  </Listbox>
                </template>
              </div>

              <!-- Select -->
              <Dropdown
                  v-else-if="field.type==='select'"
                  v-model="entry[field.model]"
                  :options="resolveOptions(field)"
                  :optionLabel="field.text || 'label'"
                  :optionValue="field.value || 'value'"
                  class="w-100"
                  :placeholder="field.placeholder"
                  showClear
                  filter
                  :disabled="field.disable"
                  @change="markDirty(entry)"
              />

              <!-- MultiSelect -->
              <MultiSelect
                  v-else-if="field.type==='multiselect'"
                  v-model="entry[field.model]"
                  :options="resolveOptions(field)"
                  :optionLabel="field.text || 'label'"
                  :optionValue="field.value || 'value'"
                  class="w-100"
                  display="chip"
                  filter
                  :disabled="field.disable"
                  @change="markDirty(entry)"
              />

              <!-- Fallback (readonly json) -->
              <Textarea v-else v-model="entry[field.model]" class="w-100" autoResize rows="4" readonly :disabled="field.disable"/>

              <small v-if="field.help" class="opacity-70">{{ field.help }}</small>
            </div>
          </template>

          <!-- DİL & HIZLI AKSİYONLAR -->
          <div class="col-span-12 flex items-center justify-between mt-2">
            <div class="flex items-center gap-2">
              <Tag v-if="entry.__dirty" severity="warning" value="Değişti" />
              <Tag v-if="entry.version" severity="secondary" :value="`ver: ${shortVersion(entry.version)}`" />
            </div>
            <div class="flex items-center gap-2">
              <Button v-if="entry.__isNew" icon="pi pi-refresh" label="Sekmeyi Kaydet" :loading="entry.__saving" size="small" @click="onSaveLanguage(entry)" />
              <Button v-if="entry.__isNew" icon="pi pi-trash" label="Sekmeyi Kaldır" severity="danger" size="small" @click="onRemoveLanguage(entry.language)" />
            </div>
          </div>
        </div>
      </TabPanel>
    </TabView>

    <template #footer>
      <div class="flex justify-between w-full">
        <div class="flex items-center gap-2">
          <Tag v-if="dirtyCount" severity="info" :value="`${dirtyCount} değişiklik`" />
        </div>
        <div class="flex gap-2">
          <Button label="Vazgeç" severity="secondary" @click="onCancel" />
          <Button label="Hepsini Kaydet"
                  icon="pi pi-check"
                  :loading="savingAll"
                  :disabled="hasNewUnsaved"
                  @click="onSaveAll" />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script>
import InputText from "primevue/inputtext";
import Button from "primevue/button";

export default {
  name: 'DynamicLangDialogOptions',
  components: {Button, InputText},
  props: {
    modelValue: { type: Boolean, required: true },
    astrologId: { type: [String, Number], required: true },
    endpoints: {
      type: Object,
      default: () => ({
        get: (id) => `/admin/astrolog/translation/${id}`,
        post: () => `/admin/astrolog/translation`,
        put: () => `/admin/astrolog/translation`,
        tags: {
          get: (id, lang) => `/api/admin/astrolog/tag/${id}/${lang}`,
          post: () => `/api/admin/astrolog/tag`,
          delete: (tagId) => `/api/admin/astrolog/tag/${tagId}`
        }
      })
    },
    supportedLangs: { type: Array, default: () => ['tr','en','fr','es','pt'] },
    requestHeaders: { type: Object, default: () => ({}) },
    autoLoad: { type: Boolean, default: true },
    dialogTitle: { type: String, default: 'Çok Dilli Dinamik Form' },
    initialLanguage: { type: String, default: null },
    formFields: {
      type: Array,
      default: () => ([
        { label: 'Dil', type: 'text', model: 'language', required: true, readonly: true }
      ])
    }
  },
  emits: ['update:modelValue', 'loaded', 'saved', 'saved:lang'],
  data() {
    return {
      visible: this.modelValue,
      loading: false,
      savingAll: false,
      activeIndex: 0,
      formEntries: [],
      newAstrologTag: {},
      astrologTags: [],
    }
  },
  computed: {
    presentLangs() { return new Set(this.formEntries.map(e => e.language)) },
    remainingLangs() { return this.supportedLangs.filter(l => !this.presentLangs.has(l)) },
    dirtyCount() { return this.formEntries.filter(e => e.__dirty).length },
    hasNewUnsaved(){ return this.formEntries.some(e => e.__isNew) }
  },
  watch: {
    modelValue(v){ this.visible = v },
    visible(v){ this.$emit('update:modelValue', v) }
  },
  methods: {
    onShow(){ if (this.autoLoad) this.fetchEntries() },
    async fetchEntries(){
      this.loading = true
      try {
        const data = await this.$appAxios.get(this.endpoints.get(this.astrologId), { headers: { 'Content-Type': 'application/json', ...this.requestHeaders } })
        console.log("data",data)
        this.formEntries = this.normalizeEntries(data.data)
        if (this.initialLanguage) {
          const idx = this.formEntries.findIndex(e => e.language === this.initialLanguage)
          this.activeIndex = idx >= 0 ? idx : 0
        } else {
          this.activeIndex = 0
        }
        const activeLang = this.formEntries?.[this.activeIndex]?.language;
        this.getTags(activeLang);
        this.$emit('loaded', this.formEntries)
      } catch (e) {
        console.error('Fetch error', e)
        this.$toast?.add?.({ severity: 'error', summary: 'Yükleme hatası', detail: 'Kayıtlar alınamadı', life: 3000 })
      } finally { this.loading = false }
    },
    normalizeEntries(list){
      const map = new Map()
      ;(list||[]).forEach(r => {
        if (!r?.language) return
        const base = { language: r.language, version: r.version ?? r.etag ?? null, __dirty:false, __saving:false }
        for (const f of this.formFields) {
          const key = f.model
          if (key === 'language') { base.language = r.language; continue }
          let val = r[key]
          if (val === undefined) val = this.defaultValueOf(f)
          base[key] = val
        }
        map.set(r.language, base)
      })
      return Array.from(map.values())
    },
    defaultValueOf(field){
      if (field.default !== undefined) return field.default
      switch(field.type){
        case 'text': case 'textarea': return ''
        case 'number': return 0
        case 'switch': return false
        case 'chips': return []
        case 'select': return null
        case 'multiselect': return []
        default: return null
      }
    },
    resolveOptions(field){
      if (typeof field.options === 'function') return field.options(this)
      return field.options || []
    },
    markDirty(entry){ entry.__dirty = true },
    tabHeader(entry){
      return `${entry.language?.toUpperCase?.() || ''}${entry.__dirty ? ' *' : ''}` },
    onAddLanguage(lang){
      if (!lang) return
      if (this.presentLangs.has(lang)) {
        this.$toast?.add?.({ severity:'warn', summary:'Zaten ekli', detail:`${lang} mevcut`, life:2500 })
        return
      }
      const base = { language: lang, version: null, __dirty:true, __saving:false, __isNew:true }
      for (const f of this.formFields) {
        if (f.model === 'language') continue
        base[f.model] = this.defaultValueOf(f)
      }
      this.formEntries.push(base)
      this.activeIndex = this.formEntries.length - 1
    },
    onRemoveLanguage(lang){
      const idx = this.formEntries.findIndex(e => e.language === lang)
      if (idx >= 0) {
        this.formEntries.splice(idx, 1)
        if (this.activeIndex >= this.formEntries.length) this.activeIndex = Math.max(0, this.formEntries.length - 1)
      }
    },
    validateEntry(entry){
      for (const f of this.formFields) {
        if (!f.required) continue
        const v = entry[f.model]
        const empty = v === null || v === undefined || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length===0)
        if (empty) return { ok:false, field:f }
      }
      return { ok:true }
    },
    payloadOf(entry){
      const obj = { language: entry.language }

      for (const f of this.formFields) {
        if (f.model === 'language') continue
        obj[f.model] = entry[f.model]
      }

      obj.id = entry.id ?? null
      obj.astrologInfoId = this.astrologId ?? null
      obj.version = entry.version ?? null

      return obj
    },
    async onSaveLanguage(entry){
      const v = this.validateEntry(entry)
      if (!v.ok){
        this.$toast?.add?.({ severity:'error', summary:'Eksik alan', detail:`${v.field.label} gerekli`, life:3000 })
        return
      }
      entry.__saving = true
      try {
        const res = await this.$appAxios.post(this.endpoints.post(), this.payloadOf(entry), {
          headers: { 'Content-Type': 'application/json', ...(entry.version ? { 'If-Match': entry.version } : {}), ...this.requestHeaders },
        })
        if (res.status === 409) {
          this.$toast?.add?.({ severity:'warn', summary:'Çakışma', detail:'Başka bir güncelleme algılandı', life:4000 })
          return
        }
        const updated = await res
        entry.version = updated?.version ?? updated?.etag ?? entry.version
        entry.id = updated?.id ?? entry.id
        entry.__dirty = false
        entry.__isNew = false
        this.$emit('saved:lang', { language: entry.language, entry: updated })
        this.$toast?.add?.({ severity:'success', summary:'Kaydedildi', detail: entry.language.toUpperCase(), life:2000 })
      } catch (e) {
        console.error('save lang error', e)
        this.$toast?.add?.({ severity:'error', summary:'Hata', detail:`${entry.language} kaydedilemedi`, life:3000 })
      } finally { entry.__saving = false }
    },
    async onSaveAll() {
      const newUnsaved = this.formEntries.filter(e => e.__isNew)
      if (newUnsaved.length) {
        const langs = newUnsaved.map(x => x.language.toUpperCase()).join(', ')
        this.$toast?.add?.({
          severity:'warn',
          summary:'Önce yeni dilleri kaydedin',
          detail:`İlk kez eklenen diller: ${langs}. Önce “Sekmeyi Kaydet” ile oluşturun.`,
          life:3500
        })
        return
      }
      const dirty = this.formEntries.filter(e => e.__dirty && !e.__isNew)
      if (!dirty.length) {
        this.$toast?.add?.({ severity: 'info', summary: 'Güncel', detail: 'Kaydedilecek değişiklik yok', life: 2000 })
        return
      }

      this.savingAll = true
      try {
        const tasks = dirty.map(entry => {
          const body = this.payloadOf(entry)
          body.tags = []
          console.log("body",body)
          return this.$appAxios.put(this.endpoints.put(), body, {
            headers: { 'Content-Type': 'application/json', ...this.requestHeaders },
          })
              .then(res => ({ ok: true, entry, data: res?.data }))
              .catch(err => ({ ok: false, entry, err }))
        })

        const results = await Promise.allSettled(tasks)

        let okCount = 0, failCount = 0
        results.forEach(r => {
          if (r.status !== 'fulfilled') { failCount++; return }
          const v = r.value
          if (!v.ok) { failCount++; return }

          const updated = Array.isArray(v.data) ? v.data[0] : (v.data?.response ?? v.data ?? {})
          const lang = updated?.language || v.entry.language

          const local = this.formEntries.find(x => x.language === lang)
          if (local) {
            local.version = updated?.version ?? updated?.etag ?? local.version
            local.__dirty = false
          }
          okCount++
        })

        if (okCount > 0) {
          this.$emit('saved', { count: okCount })
          this.$toast?.add?.({
            severity: failCount ? 'warn' : 'success',
            summary: 'Hepsi kaydet',
            detail: failCount ? `${okCount} başarılı, ${failCount} hatalı` : `${okCount} dil kaydedildi`,
            life: 2500
          })
        } else {
          this.$toast?.add?.({ severity: 'error', summary: 'Hata', detail: 'Kaydedilemedi', life: 3000 })
        }
      } catch (e) {
        console.error('save all error', e)
        this.$toast?.add?.({ severity: 'error', summary: 'Hata', detail: 'Toplu kaydetmede sorun oluştu', life: 3000 })
      } finally {
        this.savingAll = false
      }
    },
    onCancel(){ this.visible = false },
    onTabChange(e){
      const idx = e.index ?? 0;
      const lang = this.formEntries?.[idx]?.language;
      this.getTags(lang);
    },
    getTags(language){
      if (!this.astrologId || !language) return;

      this.$appAxios
          .get(`admin/astrolog/tag/${this.astrologId}/${language}`)
          .then(resp => {
            let raw = resp?.data;
            if (Array.isArray(raw?.response)) raw = raw.response;
            if (!Array.isArray(raw)) raw = [];

            this.astrologTags = raw.map(t => ({
              id: t.id ?? t.tagId ?? t.tagID,
              tag: t.tag ?? t.name ?? t.label ?? ''
            })).filter(x => x.id && x.tag);

            this.newAstrologTag = { tag: '' };
          })
          .catch(err => {
            console.error('getTags error', err);
            this.astrologTags = [];
          });
    },
    shortVersion(v){ if (!v) return ''; const s = String(v); return s.length > 8 ? s.slice(0,8)+'…' : s },
    addNewAstrologTag(entry) {
      const payload = {
        userInfoId: this.astrologId,
        tag: this.newAstrologTag?.tag?.trim(),
        language: entry?.language
      };
      if (!payload.tag) return;

      this.$appAxios.post('admin/astrolog/tag', payload)
          .then(() => {
            this.newAstrologTag.tag = '';
            this.getTags(entry.language);
          })
          .catch(err => {
            console.log(err);
            this.$toast?.add?.({ severity:'error', summary:'Error', detail: err?.error || 'Eklenemedi', life:3000 });
          });
    },
    deleteAstrologTag(tagId) {
      if (!tagId) return;
      this.$appAxios.delete(`admin/astrolog/tag/${tagId}`)
          .then(() => {
            const lang = this.formEntries?.[this.activeIndex]?.language;
            this.getTags(lang);
          })
          .catch(err => {
            console.log(err);
            this.$toast?.add?.({ severity:'error', summary:'Error', detail: err?.error || 'Silinemedi', life:3000 });
          });
    }
  }
}
</script>

<style scoped>
:deep(.p-dialog-header){ font-weight:600 }
:deep(.p-tabview-nav){ flex-wrap:wrap }
</style>
